import Vue from 'vue';
import Vuex from 'vuex';
import { intersection } from 'lodash';
import ScriptLoaderModule from '@/store/scriptLoaderModule';

Vue.use(Vuex);

export default new Vuex.Store({
  modules: {
    scriptLoader: ScriptLoaderModule,
  },
  state: {
    account: {},
    ApiQueries: 0,
    drawerShow: true,
    dialogs: {},
    dialogs_data: {},
    currentUserId: null,
    previousUserId: null,
    snackbar: {
      text: null,
      show: false,
    },
    restoreEmail: null,
    createJoinDialog: false,
    useTermsDialog: false,
    createPaymentDialog: null,
    bpProgressDialog: null,
    UISettings: {},
    snackInfoMessage: null,
    snackErrorMessage: null,
  },
  getters: {
    lang: state => (!state.UISettings ? 'ru' : state.UISettings.lang),
    accountAuthorized: state => !!(state.account && state.account.account),
    accountData: state => state.account.account,
    restoreEmail: state => state.restoreEmail,
    currentUser: (state) => {
      const { currentUserId } = state;
      if (state.account && state.account.users && currentUserId) {
        return state.account.users.find(u => u.id === currentUserId);
      }
      return null;
    },
    users: state => (state.account && state.account.users ? state.account.users.slice()
      .sort((a, b) => (a.name > b.name ? 1 : -1)) : null),
    userSubscriptions: state => ((state.currentUserId && state.account.subscriptions)
      ? state.account.subscriptions.filter(sub => sub.userId === state.currentUserId)
      : []),
    usersCount: state => ((state.account && state.account.users) ? state.account.users.length : 0),
    classes: (state) => {
      const {
        account,
        UISettings,
        currentUserId,
      } = state;

      if (!UISettings || !account || !account.users || !currentUserId) {
        return [];
      }

      const {
        viewFilials,
        viewClasses,
      } = UISettings;

      const currentUser = account.users.find(u => u.id === currentUserId);

      let filialIds;
      if (viewFilials === 'own') {
        filialIds = account.filials.filter(f => currentUser.filials.includes(f.id)).map(f => f.id);
      } else {
        filialIds = account.filials.map(f => f.id);
      }

      const classes = account.classes.filter(cl => filialIds.includes(cl.filialId));

      if (viewClasses === 'all') {
        return classes;
      }

      const joinedClassesIds = currentUser.joins.map(j => j.classId);
      return classes.filter(cl => joinedClassesIds.includes(cl.id));
    },
    courses: state => ((state.account && state.account.courses) ? state.account.courses : []),
    filials: (state) => {
      const { currentUserId, account, UISettings } = state;

      const restrictFilials = UISettings && UISettings.viewFilials === 'own';

      if (!restrictFilials || !account || !account.users || !currentUserId) {
        return (account && account.filials) ? account.filials : [];
      }

      const currentUser = account.users.find(u => u.id === currentUserId);

      return account.filials.filter(f => currentUser.filials.includes(f.id));
    },
    subscriptionTypes: (state) => {
      if (!state.account) {
        return [];
      }

      return state.account.subscriptionTypes || [];
    },
    onlinePaymentIntegrations: (state) => {
      if (!state.account) {
        return [];
      }

      return state.account.onlinePaymentIntegrations || [];
    },
    rooms: (state) => {
      const { currentUserId, account, UISettings } = state;
      const restrictFilials = UISettings && UISettings.viewFilials === 'own';
      if (!restrictFilials || !account || !account.users || !currentUserId) {
        return state.account && state.account.rooms ? state.account.rooms : [];
      }

      const currentUser = account.users.find(({ id }) => id === currentUserId);
      return state.account.rooms.filter(({ filialId }) => currentUser.filials.includes(filialId));
    },
    managers: (state) => {
      const { currentUserId, account, UISettings } = state;
      const restrictFilials = UISettings && UISettings.viewFilials === 'own';
      if (!restrictFilials || !account || !account.users || !currentUserId) {
        return (state.account && state.account.managers)
          ? state.account.managers.sort((a, b) => (a.name > b.name ? 1 : -1))
          : [];
      }

      const currentUser = account.users.find(({ id }) => id === currentUserId);
      return state.account.managers.filter(
        ({ teachFilialIds: ids }) => intersection(ids, currentUser.filials).length,
      );
    },
    currency: state => ((state.account && state.account.company) ? state.account.company.currency : ''),
    currencyCode: state => ((state.account && state.account.company)
      ? state.account.company.currencyCode : null),
    createJoinDialog: state => state.createJoinDialog,
    useTermsDialog: state => state.useTermsDialog,
    createPaymentDialog: state => state.createPaymentDialog,
    bpProgressDialog: state => state.bpProgressDialog,
    UISettings: state => state.UISettings,
    snackInfoMessage: state => state.snackInfoMessage,
    snackErrorMessage: state => state.snackErrorMessage,
    jivoSiteWidgetUrl: state => state.account.jivoSiteWidgetUrl,
    previousUserId: state => state.previousUserId,
    allowOnlinePayment: state => state.UISettings
    && state.UISettings.onlinePayment && state.UISettings.onlinePayment.enabled,
    allowedClassIds: (state, getters) => {
      const { classes, currentUser, userSubscriptions } = getters;

      const { joins } = currentUser;
      const joinClassIds = [];
      joins.forEach((j) => {
        const debtSubs = userSubscriptions
          .filter(sub => sub.classIds.includes(j.classId) && sub.statusId !== 4)
          .filter(sub => (sub.payed < sub.price) && sub.remindDate);

        const debtInvoices = j.invoices
          ? j.invoices.filter(invoice => invoice.price > invoice.payed)
          : null;

        // Если у ученика статус Отказался/Завершён и у него не осталось долгов перед группой
        if (j.status === 'over' && !debtSubs.length && !debtInvoices.length) {
          return;
        }

        joinClassIds.push(j.classId);
      });

      const classIds = classes
        .filter(cl => cl.onlinePayment.enabled)
        .filter(cl => joinClassIds.indexOf(cl.id) > -1)
        .map(({ id }) => id);

      return classIds;
    },
  },
  mutations: {
    increaseQuery: (state) => {
      state.ApiQueries += 1;
    },
    decreaseQuery: (state) => {
      state.ApiQueries -= 1;
    },
    setDrawerState: (state, value) => {
      state.drawerShow = value;
    },
    showSnackbar: (state, text) => {
      state.snackbar.text = text;
      state.snackbar.show = true;
    },
    hideSnackbar: (state) => {
      state.snackbar.show = false;
    },
    setAccountData: (state, value) => {
      state.account = value;
    },
    setRestoreEmail: (state, email) => {
      state.restoreEmail = email;
    },
    setCurrentUserId: (state, userId) => {
      state.previousUserId = state.currentUserId;
      state.currentUserId = userId;
    },
    setCreateJoinDialog: (state, value) => {
      state.createJoinDialog = value;
    },
    setCreatePaymentDialog: (state, value) => {
      state.createPaymentDialog = value;
    },
    setBpProgressDialog: (state, value) => {
      state.bpProgressDialog = value;
    },
    setUseTermsDialog: (state, value) => {
      state.useTermsDialog = value;
    },
    setUISettings: (state, value) => {
      state.UISettings = value;
    },
    setSnackInfoMessage: (state, value) => {
      state.snackInfoMessage = value;
    },
    setSnackErrorMessage: (state, value) => {
      state.snackErrorMessage = value;
    },
  },
});
